import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, X, Sparkles, Briefcase, FileText, Phone, Users, Brain, Settings, ChevronRight, PartyPopper } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'add_position', label: 'Create your first position', desc: 'Add an open position with client details and JD', icon: Briefcase, path: '/positions', color: 'text-blue-600 bg-blue-50' },
  { key: 'upload_resume', label: 'Upload a resume', desc: 'Add a candidate resume against a position', icon: FileText, path: '/resumes', color: 'text-indigo-600 bg-indigo-50' },
  { key: 'make_ccr_call', label: 'Log a calling report', desc: 'Record your first candidate calling activity', icon: Phone, path: '/ccr', color: 'text-violet-600 bg-violet-50' },
  { key: 'invite_team_member', label: 'Invite a team member', desc: 'Add a recruiter or team lead to your organization', icon: Users, path: '/user-management', color: 'text-emerald-600 bg-emerald-50' },
  { key: 'explore_ai', label: 'Try RecruiterIQ AI', desc: 'Generate a JD or screen a resume with AI', icon: Brain, path: '/recruiter-iq', color: 'text-amber-600 bg-amber-50' },
  { key: 'configure_org_settings', label: 'Configure organization settings', desc: 'Set your organization name, currency, language and timezone', icon: Settings, path: '/org-settings', color: 'text-cyan-600 bg-cyan-50' },
];

export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }
    axios.get(`${BACKEND_URL}/api/onboarding/checklist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDismiss = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/onboarding/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(prev => ({ ...prev, dismissed: true }));
    } catch (err) { console.error("./components/OnboardingChecklist.jsx:", err); }
  };

  const handleDismissWelcome = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/onboarding/dismiss-welcome`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(prev => ({ ...prev, welcome_dismissed: true }));
    } catch (err) { console.error("./components/OnboardingChecklist.jsx:", err); }
  };

  if (loading || !data || data.dismissed) return null;

  const { steps, completed, total, progress, welcome_dismissed } = data;
  const allDone = completed === total;

  return (
    <div className="mb-8 space-y-4" data-testid="onboarding-checklist">
      {/* Welcome Banner */}
      {!welcome_dismissed && (
        <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 rounded-xl p-5 sm:p-6 text-white overflow-hidden" data-testid="welcome-banner">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="relative z-10 flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5" />
                <h2 className="text-lg sm:text-xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif' }}>Welcome to Recruitizr!</h2>
              </div>
              <p className="text-sm text-blue-100 max-w-lg">
                Let's get you set up. Complete these 6 quick steps to unlock the full power of your recruitment platform.
              </p>
            </div>
            <button onClick={handleDismissWelcome} className="text-white/60 hover:text-white shrink-0 ml-4" data-testid="dismiss-welcome">
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Checklist Card */}
      {allDone ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center justify-between" data-testid="onboarding-complete">
          <div className="flex items-center gap-3">
            <PartyPopper className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="font-bold text-emerald-900">All set! You're ready to recruit.</p>
              <p className="text-xs text-emerald-700">You've completed all onboarding steps. Happy hiring!</p>
            </div>
          </div>
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={handleDismiss} data-testid="dismiss-checklist">
            Dismiss
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="onboarding-steps">
          {/* Progress Bar */}
          <div className="px-5 pt-4 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <p className="text-sm font-bold text-slate-900">Getting Started</p>
              <span className="text-xs text-slate-500">{completed}/{total} complete</span>
            </div>
            <button onClick={handleDismiss} className="text-xs text-slate-400 hover:text-slate-600" data-testid="dismiss-checklist">
              Skip for now
            </button>
          </div>
          <div className="mx-5 mb-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-violet-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>

          {/* Steps */}
          <div className="divide-y divide-slate-100">
            {STEPS.map(step => {
              const done = steps[step.key];
              const Icon = step.icon;
              return (
                <button
                  key={step.key}
                  data-testid={`onboard-step-${step.key}`}
                  onClick={() => !done && navigate(step.path)}
                  className={`w-full flex items-center gap-4 px-5 py-3.5 text-left transition-colors ${done ? 'bg-slate-50/50' : 'hover:bg-blue-50/50 cursor-pointer'}`}
                >
                  {done ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-slate-300 shrink-0" />
                  )}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${step.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${done ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{step.label}</p>
                    <p className="text-xs text-slate-500">{step.desc}</p>
                  </div>
                  {!done && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
